"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  num: "01",
  title: "Application & Discovery",
  desc: "Submit your initial documentation through our secure portal. We analyze your financial profile to determine the optimal lending strategy for your homeownership goals."
}, {
  num: "02",
  title: "Processing & Verification",
  desc: "Our dedicated team meticulously reviews every detail, coordinating with appraisers and title companies to ensure your file meets all regulatory and architectural standards."
}, {
  num: "03",
  title: "Underwriting Approval",
  desc: "Beacon's expert underwriters perform a final risk assessment. We work transparently to clear any remaining conditions, moving your loan toward a firm commitment."
}, {
  num: "04",
  title: "Closing & Funding",
  desc: "The final milestone where documents are signed and funds are disbursed. We celebrate as you receive the keys to your new property, backed by secure financing."
}];
function TimelineItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4,
    once: false
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className="relative flex flex-col md:flex-row items-center justify-between w-full mb-24 md:mb-40 last:mb-0 group">
      {}
      <div className="absolute left-[15px] md:left-1/2 w-4 h-4 rounded-md bg-background border-2 transform -translate-x-1/2 z-20 transition-colors duration-500 ease-in-out" style={{
      borderColor: isVisible ? "var(--primary)" : "var(--border)",
      backgroundColor: isVisible ? "var(--primary)" : "var(--background)",
      boxShadow: isVisible ? "0 0 15px var(--primary)" : "none"
    }} />

      {}
      <div className={`w-full md:w-[45%] pl-16 md:pl-0 ${isEven ? "md:text-right md:pr-16" : "md:order-2 md:text-left md:pl-16"}`}>
        <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className={`text-7xl md:text-9xl font-bold font-mono leading-none mb-6 transition-all duration-1000 ease-out origin-center ${isVisible ? "scale-100 text-primary/10" : "scale-50 text-muted/5"}`}>
            {step.num}
          </div>

          {}
          <div className={`transition-opacity duration-1000 delay-300 p-6 rounded-md border border-primary/5 bg-white/50 backdrop-blur-sm shadow-lg ${isVisible ? "opacity-100" : "opacity-0"}`}>
            <h3 className="text-2xl md:text-4xl font-bold font-default mb-4 tracking-tight text-primary">
              {step.title}
            </h3>
            <p className="text-foreground text-lg md:text-xl leading-relaxed font-default">
              {step.desc}
            </p>
          </div>
        </div>
      </div>

      {}
      <div className={`hidden md:block w-[45%] ${isEven ? "md:order-2" : ""}`} />
    </div>;
}
export default function ThePathToClosing() {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [fillHeight, setFillHeight] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startOffset = windowHeight / 2;
      const scrolledPastStart = startOffset - rect.top;
      if (scrolledPastStart < 0) {
        setFillHeight(0);
      } else {
        const progress = scrolledPastStart / rect.height * 100;
        setFillHeight(Math.min(Math.max(progress, 0), 100));
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="223698" id="the-path-to-closing" className="bg-background text-foreground py-24 md:py-48 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div className="text-center mb-32 md:mb-48 relative z-10">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold font-default tracking-tighter mb-8 uppercase">
            The Path to <span className="text-primary">Closing</span><br />
            Journey
          </h2>
          <p className="text-xl md:text-2xl text-foreground/80 max-w-3xl mx-auto font-default font-medium">
            A structured, architectural approach to translating your homeownership vision into a high-impact financial reality.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto" ref={timelineRef}>
          
          {}
          <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-[2px] bg-border transform -translate-x-1/2 z-0" />
          
          {}
          <div className="absolute left-[15px] md:left-1/2 top-0 w-[2px] bg-primary transform -translate-x-1/2 z-10 transition-all duration-75 ease-linear shadow-md" style={{
          height: `${fillHeight}%`
        }} />

          {}
          <div className="relative z-20 pt-10 pb-10">
            {steps.map((step, index) => <TimelineItem key={step.num} step={step} index={index} />)}
          </div>
          
        </div>
      </div>
    </section>;
}